import React, {FC, useEffect, useState} from 'react';

import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {saveUserPreferencesAction} from '../../../../store/data/preferences/preferencesActions';
import {getActivationPreferences} from '../../../../store/data/preferences/preferencesSelectors';
import {OnboardingGuidesEnum} from '../../../onboarding/guide/onboardingGuideTypes';
import {DismissSetupGuideDialog} from '../../../onboarding/setupGuide/dismissSetupGuideDialog';
import {FluSetupGuidePopover} from '../../../onboarding/setupGuide/fluSetupGuidePopover';
import {SetupGuideTopBarButton} from '../../../onboarding/setupGuide/setupGuideTopBarButton';
import {
  FluSetupGuideConcreteScreen,
  FluSetupGuideScreensEnum,
} from '../../../onboarding/setupGuide/setupGuideTypes';
import {useFluOnboardingTasks} from '../../../onboarding/tasks/onboardingTaskHelpers';
import {FluSetupGuideTasksAndProgress} from '../../../onboarding/tasks/onboardingTypes';
import {useOnboardingModalState} from './onboardingContext';

/*
 * Props.
 */

interface FluSetupGuideProps {
  className?: string;
  panelId: AppPanelId;
}

/*
 * Component.
 */

export const FluSetupGuide: FC<FluSetupGuideProps> = (props) => {
  const {panelId, className} = props;
  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();

  const tasks = useFluOnboardingTasks({panelId});

  const {onboardingGuide, renderOnboardingGuideTransition, setOnboardingGuide} = useOnboardingModalState();

  const isFluSetupGuideOpen = onboardingGuide === OnboardingGuidesEnum.FRONT_LINE_USER_SETUP;

  const [isDismissPermanentlyDialogOpen, setIsDismissPermanentlyDialogOpen] = useState(false);

  const activationPreferences = useAppSelector(getActivationPreferences);

  const [defaultScreen, setDefaultScreen] = useState<FluSetupGuideConcreteScreen>(
    computeDefaultScreen(tasks),
  );

  useEffect(() => {
    setDefaultScreen(computeDefaultScreen(tasks));
  }, [isFluSetupGuideOpen]);

  return (
    <>
      <div>
        <SetupGuideTopBarButton
          className={className}
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_SCREEN_OPEN,
            );
            setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_SETUP);
          }}
          taskProgress={tasks.start.progress}
          tasksAreComplete={false}
        />
      </div>

      {renderOnboardingGuideTransition(OnboardingGuidesEnum.FRONT_LINE_USER_SETUP, (styles) => (
        <FluSetupGuidePopover
          panelId={panelId}
          styles={styles}
          tasks={tasks}
          defaultScreen={defaultScreen}
          onDismissPermanentlyClick={() => {
            setOnboardingGuide(undefined);
            setIsDismissPermanentlyDialogOpen(true);
          }}
          onClose={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_SCREEN_CLOSE,
            );
            setOnboardingGuide(undefined);
          }}
        />
      ))}
      {isDismissPermanentlyDialogOpen && (
        <DismissSetupGuideDialog
          onClose={() => {
            setIsDismissPermanentlyDialogOpen(false);
            setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_SETUP);
          }}
          onOkClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.ADMIN_SETUP_GUIDE_DISMISS_PERMANENTLY,
            );
            dispatch(
              saveUserPreferencesAction({
                activationPreferences: {
                  ...activationPreferences,
                  isSetupGuideDismissed: true,
                },
              }),
            );
            setIsDismissPermanentlyDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

const computeDefaultScreen = (tasks: FluSetupGuideTasksAndProgress) => {
  if (tasks.start.progress.hasCompletedAllTasks) {
    return FluSetupGuideScreensEnum.MORE;
  }

  return FluSetupGuideScreensEnum.GET_STARTED;
};
